import * as React from "react";
import styled from "styled-components";
import { concatClassnames, firstDefined, splitUnit } from "./util";

const defaultMargin = "0.5rem";

export interface WSFormGridProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  margin?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  horizontal?: boolean;
  float?: boolean;
}

interface WSFormGridContextStore {
  margin?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  float?: boolean;
}

const WSFormGridContext = React.createContext<WSFormGridContextStore>(null);

interface _WSFormGridProps extends WSFormGridProps {
  innerRef: any;
}

const _WSFormGrid = (props: _WSFormGridProps) => {
  let {
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    horizontal,
    className,
    float,
    ...rest
  } = props;

  const context = React.useContext(WSFormGridContext);

  margin = firstDefined([margin, context?.margin, defaultMargin]);
  float = firstDefined([float, context?.float]);

  marginTop = firstDefined([
    marginTop,
    margin,
    context?.marginTop,
    context?.margin,
    defaultMargin,
  ]);

  marginBottom = firstDefined([
    marginBottom,
    margin,
    context?.marginBottom,
    context?.margin,
    defaultMargin,
  ]);

  marginLeft = firstDefined([
    marginLeft,
    margin,
    context?.marginLeft,
    context?.margin,
    defaultMargin,
  ]);

  marginRight = firstDefined([
    marginRight,
    margin,
    context?.marginRight,
    context?.margin,
    defaultMargin,
  ]);

  const formGridContext: WSFormGridContextStore = React.useMemo(
    () => ({
      margin,
      marginRight,
      marginLeft,
      marginBottom,
      marginTop,
      float,
    }),
    [margin, marginRight, marginLeft, marginBottom, marginTop, float]
  );

  if (float && !horizontal) {
    return (
      <WSFormGridContext.Provider value={formGridContext}>
        <FloatGrid
          {...(rest as any)}
          marginTop={half(marginTop)}
          marginBottom={half(marginBottom)}
          marginLeft={half(marginLeft)}
          marginRight={half(marginRight)}
          className={concatClassnames([
            className,
            "ws-form-grid",
            "ws-form-grid-float",
          ])}
        />
      </WSFormGridContext.Provider>
    );
  }

  if (float && horizontal) {
    return (
      <HorizontalFloatGrid
        {...(rest as any)}
        marginTop={half(marginTop)}
        marginBottom={half(marginBottom)}
        marginLeft={half(marginLeft)}
        marginRight={half(marginRight)}
        className={concatClassnames([
          className,
          "ws-form-grid",
          "ws-form-grid-float",
          "ws-form-grid-float-horizontal",
        ])}
      />
    );
  }

  if (!horizontal) {
    return (
      <WSFormGridContext.Provider value={formGridContext}>
        <Grid
          {...(rest as any)}
          marginTop={marginTop}
          marginBottom={marginBottom}
          marginLeft={marginLeft}
          marginRight={marginRight}
          className={concatClassnames([className, "ws-form-grid"])}
        />
      </WSFormGridContext.Provider>
    );
  }

  return (
    <HorizontalGrid
      {...(rest as any)}
      marginTop={marginTop}
      marginBottom={marginBottom}
      marginLeft={half(marginLeft)}
      marginRight={half(marginRight)}
      className={concatClassnames([
        className,
        "ws-form-grid",
        "ws-form-grid-horizontal",
      ])}
    />
  );
};

export const WSFormGrid = React.forwardRef<HTMLDivElement, WSFormGridProps>(
  (props, ref) => <_WSFormGrid {...props} innerRef={ref} />
);

const half = (s: string) => {
  const [value, unit] = splitUnit(s);

  return `${Number(value) * 0.5}${unit}`;
};

const FloatGrid = styled.div<WSFormGridProps>`
  margin-top: -${(props) => props.marginTop};
  margin-bottom: -${(props) => props.marginBottom};
  margin-left: -${(props) => props.marginLeft};
  margin-right: -${(props) => props.marginRight};
  display: flex;
  flex-wrap: wrap;

  > * {
    &:not(.p-button) {
      width: 100%;
    }

    &:not(.ws-form-grid) {
      max-width: ${(p) => `calc(100% - ${p.marginLeft} - ${p.marginRight})`};
    }

    margin-top: ${(props) => props.marginTop};
    margin-bottom: ${(props) => props.marginBottom};
    margin-left: ${(props) => props.marginLeft};
    margin-right: ${(props) => props.marginRight};
  }

  > .ws-form-grid-float {
    margin: 0;
  }

  .p-component:not(.p-button):not(.p-checkbox):not(.p-radiobutton) {
    width: 100%;
  }

  hr {
    margin-top: ${(props) => `calc(${props.marginTop} + ${props.marginTop})`};
    margin-bottom: ${(props) =>
      `calc(${props.marginBottom} + ${props.marginBottom})`};
  }
`;

const HorizontalFloatGrid = styled.div<WSFormGridProps>`
  display: flex;
  width: 100%;

  margin-top: 0;
  margin-bottom: 0;
  margin-left: 0;
  margin-right: 0;

  > * {
    flex-basis: 1px;
    flex-grow: 1;
    margin-top: ${(props) => props.marginTop};
    margin-bottom: ${(props) => props.marginBottom};
    margin-left: ${(props) => props.marginLeft};
    margin-right: ${(props) => props.marginRight};
  }
`;

const Grid = styled.div<WSFormGridProps>`
  > *:not(:last-child):not(.ws-form-grid-horizontal) {
    margin-bottom: ${(props) => props.marginBottom};
  }

  > *:not(:first-child):not(.ws-form-grid-horizontal) {
    margin-top: ${(props) => props.marginTop};
  }
`;

const HorizontalGrid = styled.div<WSFormGridProps>`
  display: flex;
  flex-wrap: wrap;

  margin-top: -${(props) => props.marginTop};
  margin-bottom: ${(props) => props.marginBottom};
  margin-left: -${(props) => props.marginLeft};
  margin-right: -${(props) => props.marginRight};

  > .ws-form-grid-horizontal {
    margin-top: 0;
    margin-bottom: 0;
    margin-left: 0;
    margin-right: 0;
  }

  > :not(.ws-form-grid-horizontal) {
    margin-top: ${(props) => props.marginTop};
    margin-bottom: 0;
    margin-left: ${(props) => props.marginLeft};
    margin-right: ${(props) => props.marginRight};
  }
`;
